import { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, LinearProgress, CircularProgress } from '@mui/material';
import { UploadCloud, FileText, Download, Trash2 } from 'lucide-react';
import { IconButton, EmptyState } from 'components/AppShell';
import { useSnackbar } from 'components/GlobalSnackbar';
import apiReq, { backendHost } from 'core/api';
import { getErrorMessage } from 'utils/parseApiError';
import { tokenStorage } from 'utils';
import Loader from 'components/Loader';
import {
  registerProcessEventCallback,
  unregisterProcessEventCallback,
} from 'events/webSocketHandlers';
import { uploadFileChunked, PROCESS_TYPE_CONTEXT_DOCUMENT_PARSING } from 'utils/chunkedFileUpload';

const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2 ГБ, как в саммаризаторе

const FilesTab = () => {
  const currProject = useSelector((state) => state.reducer.project.id);
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [parsingStage, setParsingStage] = useState(null);
  const pendingProcessesRef = useRef({});

  const waitForProcess = useCallback((processId) => {
    return new Promise((resolve, reject) => {
      pendingProcessesRef.current[processId] = { resolve, reject };
    });
  }, []);

  useEffect(() => {
    const handleProcessEvent = (_eventName, data) => {
      const pending = pendingProcessesRef.current[data.process_id];
      if (!pending) return;
      if (data.status === 'success' || data.status === 'uploaded') {
        setParsingStage(null);
        pending.resolve(data.process_id);
        delete pendingProcessesRef.current[data.process_id];
      } else if (data.status === 'error') {
        setParsingStage(null);
        pending.reject(new Error(data.message || 'Ошибка обработки'));
        delete pendingProcessesRef.current[data.process_id];
      } else if (data.status === 'stream') {
        // Текстовая стадия обработки (например, «Чанкинг»). Пустые тики игнорируем.
        const label = data.progress?.label?.trim();
        if (label) setParsingStage(label);
      }
    };
    registerProcessEventCallback(handleProcessEvent);
    return () => unregisterProcessEventCallback();
  }, []);

  const { data: files = [], isLoading } = useQuery({
    queryKey: ['contextDocuments', currProject],
    queryFn: () => apiReq(`projects/context-documents?project_id=${currProject}`, 'GET'),
    enabled: !!currProject,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      setUploadProgress(0);
      const fileId = await uploadFileChunked(
        file,
        currProject,
        PROCESS_TYPE_CONTEXT_DOCUMENT_PARSING,
        (pct) => setUploadProgress(Math.min(pct, 90))
      );

      const processData = await apiReq('process/context-document-parsing', 'POST', {
        file_id: fileId,
        target_type: 'project',
        target_id: currProject,
      });
      const processId = processData?.process_id ?? processData?.processId;
      if (!processId) {
        throw new Error('Сервер не вернул process_id');
      }

      setUploadProgress(92);
      await waitForProcess(processId);
      setUploadProgress(100);
      return fileId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contextDocuments', currProject] });
      showSnackbar({ message: 'Файл успешно загружен', severity: 'success' });
    },
    onError: (error) => {
      showSnackbar({
        message: getErrorMessage(error, 'Ошибка при загрузке файла'),
        severity: 'error',
      });
    },
  });

  const runUploads = useCallback(
    async (fileList) => {
      setIsUploading(true);
      setUploadProgress(0);
      try {
        for (const file of fileList) {
          if (file.size > MAX_FILE_SIZE) {
            showSnackbar({
              message: `Файл «${file.name}» слишком большой (максимум 2 ГБ)`,
              severity: 'error',
            });
            continue;
          }
          try {
            await uploadMutation.mutateAsync(file);
          } catch {
            // Тост уже в onError мутации; иначе unhandled rejection → красный экран CRA
          }
        }
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
        setParsingStage(null);
      }
    },
    [showSnackbar, uploadMutation]
  );

  const deleteMutation = useMutation({
    mutationFn: async (documentId) => {
      return apiReq(`projects/context-documents/delete?document_id=${documentId}`, 'DELETE');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contextDocuments', currProject] });
      showSnackbar({ message: 'Файл успешно удален', severity: 'success' });
    },
    onError: (error) => {
      showSnackbar({
        message: getErrorMessage(error, 'Ошибка при удалении файла'),
        severity: 'error',
      });
    },
  });

  const handleFileChange = useCallback(
    (event) => {
      const list = Array.from(event.target.files || []);
      const input = event.target;
      if (list.length) {
        runUploads(list);
      }
      if (input) input.value = '';
    },
    [runUploads]
  );

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const list = Array.from(e.dataTransfer.files);
      if (list.length) {
        runUploads(list);
      }
    },
    [runUploads]
  );

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDownload = useCallback(
    async (file) => {
      try {
        const token = tokenStorage.get();
        const downloadUrl = file.file?.id
          ? `${backendHost}/file/${file.file.id}/download`
          : `${backendHost}/projects/context-documents/${file.id}/download`;
        const res = await fetch(downloadUrl, {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(res.statusText || 'Ошибка загрузки');
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name || 'download';
        a.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        showSnackbar({
          message: getErrorMessage(error, 'Ошибка при скачивании файла'),
          severity: 'error',
        });
      }
    },
    [showSnackbar]
  );

  if (isLoading) {
    return <Loader />;
  }

  const dropBg = isDragging
    ? 'var(--shell-accent-subtle)'
    : isUploading
      ? 'var(--shell-bg-subtle)'
      : 'var(--shell-bg-surface)';
  const dropBorder = isDragging ? 'var(--shell-accent-primary)' : 'var(--shell-border-default)';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      <div
        role="button"
        tabIndex={0}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !isUploading && document.getElementById('file-input').click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !isUploading) {
            document.getElementById('file-input').click();
          }
        }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          padding: '28px 24px',
          minHeight: 140,
          border: `1px dashed ${dropBorder}`,
          borderRadius: 'var(--shell-radius-lg)',
          background: dropBg,
          cursor: isUploading ? 'default' : 'pointer',
          transition:
            'background var(--shell-transition-base), border-color var(--shell-transition-base)',
          textAlign: 'center',
          outline: 'none',
        }}
      >
        <input
          id="file-input"
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        {isUploading ? (
          <>
            <CircularProgress
              size={28}
              thickness={4}
              sx={{ color: 'var(--shell-accent-primary)' }}
            />
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--shell-text-primary)' }}>
              Загрузка и обработка…
            </div>
            <div style={{ width: '100%', maxWidth: 360 }}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'var(--shell-bg-hover)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'var(--shell-accent-primary)',
                    borderRadius: 3,
                  },
                }}
              />
            </div>
            <div style={{ fontSize: 12, color: 'var(--shell-text-tertiary)' }}>
              {uploadProgress < 92
                ? 'Загрузка по частям…'
                : parsingStage || 'Ожидание сервиса обработки…'}
            </div>
          </>
        ) : (
          <>
            <UploadCloud
              size={28}
              strokeWidth={1.5}
              color={isDragging ? 'var(--shell-accent-primary)' : 'var(--shell-text-tertiary)'}
            />
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--shell-text-primary)',
              }}
            >
              Перетащите файлы сюда или нажмите для выбора
            </div>
            <div style={{ fontSize: 12, color: 'var(--shell-text-tertiary)', maxWidth: 480 }}>
              Любые текстовые форматы, несколько файлов сразу, до 2 ГБ
            </div>
          </>
        )}
      </div>

      {files.length > 0 ? (
        <div
          style={{
            border: '1px solid var(--shell-border-subtle)',
            borderRadius: 'var(--shell-radius-lg)',
            overflow: 'hidden',
            background: 'var(--shell-bg-surface)',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              fontSize: 13,
            }}
          >
            <thead>
              <tr>
                {['', 'Название', 'Размер', 'Дата загрузки', ''].map((label, i) => (
                  <th
                    key={i}
                    style={{
                      textAlign: i === 4 ? 'right' : 'left',
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                      color: 'var(--shell-text-tertiary)',
                      background: 'var(--shell-bg-subtle)',
                      borderBottom: '1px solid var(--shell-border-subtle)',
                      padding: '0 12px',
                      height: 36,
                      width: i === 0 ? 32 : i === 4 ? 84 : undefined,
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr
                  key={file.id}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--shell-bg-subtle)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                  style={{ transition: 'background var(--shell-transition-fast)' }}
                >
                  <td
                    style={{
                      padding: '0 12px',
                      height: 40,
                      borderBottom: '1px solid var(--shell-border-subtle)',
                      width: 32,
                    }}
                  >
                    <FileText size={16} strokeWidth={1.75} color="var(--shell-text-tertiary)" />
                  </td>
                  <td
                    style={{
                      padding: '0 12px',
                      height: 40,
                      borderBottom: '1px solid var(--shell-border-subtle)',
                    }}
                  >
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => handleDownload(file)}
                      onKeyDown={(e) => e.key === 'Enter' && handleDownload(file)}
                      style={{
                        color: 'var(--shell-text-primary)',
                        cursor: 'pointer',
                        fontWeight: 500,
                        borderBottom: '1px dashed transparent',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--shell-accent-primary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--shell-text-primary)';
                      }}
                    >
                      {file.name}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: '0 12px',
                      height: 40,
                      borderBottom: '1px solid var(--shell-border-subtle)',
                      color: 'var(--shell-text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatFileSize(file.file?.size ?? file.content_length)}
                  </td>
                  <td
                    style={{
                      padding: '0 12px',
                      height: 40,
                      borderBottom: '1px solid var(--shell-border-subtle)',
                      color: 'var(--shell-text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatDate(file.created_at)}
                  </td>
                  <td
                    style={{
                      padding: '0 8px',
                      height: 40,
                      borderBottom: '1px solid var(--shell-border-subtle)',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      width: 84,
                    }}
                  >
                    <IconButton
                      size="sm"
                      label="Скачать"
                      onClick={() => handleDownload(file)}
                      icon={<Download size={14} strokeWidth={1.75} />}
                    />
                    <IconButton
                      size="sm"
                      tone="danger"
                      label="Удалить"
                      onClick={() => deleteMutation.mutate(file.id)}
                      disabled={deleteMutation.isLoading}
                      icon={<Trash2 size={14} strokeWidth={1.75} />}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          title="Файлов пока нет"
          description="Загрузите документы через зону выше — они появятся здесь и станут доступны для подключения к чату."
        />
      )}
    </Box>
  );
};

export default FilesTab;
